import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Grid,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ImageModal from "components/ImageModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

// Legacy Firebase implementation kept during the backend migration:
// import { getDatabase, ref, get, update } from "firebase/database";
// import socketService from "socket/SocketService";

const normalizeText = (value) => String(value ?? "").trim();

const normalizeRewardValue = (value) => {
  const parsed = String(value ?? "").replace(/[^0-9]/g, "");
  return Number(parsed || 0);
};

const normalizeAmountValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = normalizeRewardValue(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeTipDetails = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value;
};

const normalizeMixedList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const mapAssetToRow = async (asset, fetchUserProfile) => {
  const userId = asset?.userId || "";
  const userProfile = await fetchUserProfile(userId);

  return {
    assetId: asset?.id || asset?._id || asset?.assetId || "",
    userId,
    name: userProfile?.userName || userProfile?.fullName || userProfile?.name || "N/A",
    phone: userProfile?.userPhone || userProfile?.phone || "N/A",
    email: userProfile?.userEmail || userProfile?.email || "N/A",
    state: userProfile?.state || userProfile?.userState || "",
    vehicleType: asset?.assetType || "N/A",
    brand: asset?.brandModel || "N/A",
    plate: asset?.plateNumber || "N/A",
    color: asset?.color || "N/A",
    photo: Array.isArray(asset?.vehicleImgs) ? asset.vehicleImgs : normalizeMixedList(asset?.vehicleImgs),
    ownershipImg: asset?.ownershipImg || "",
    proof: asset?.proof || "",
    location: asset?.location || "N/A",
    datetime: asset?.dateMissing || asset?.submittedAt || asset?.createdAt || "N/A",
    reward: normalizeRewardValue(asset?.reward),
    tipDetails: normalizeTipDetails(
      asset?.tipDetails ?? asset?.tipReport ?? asset?.tip ?? asset?.tips ?? null,
    ),
    tipAmount: normalizeAmountValue(
      asset?.tipAmount ??
        asset?.tip?.amount ??
        asset?.tipDetails?.amount ??
        asset?.tipReport?.amount ??
        asset?.tip?.value ??
        asset?.tipDetails?.value ??
        asset?.tipReport?.value ??
        asset?.userTip ??
        asset?.tippedAmount,
    ),
    caseId: asset?.caseId || "N/A",
    status: Boolean(asset?.status),
    adminAddReward: Number(asset?.adminAddReward || 0),
    assetsFound: Boolean(asset?.assetsFound),
    additionalInfo: asset?.additionalInfo || "N/A",
    reports: asset?.reports,
    rawAsset: asset,
  };
};

const HelpTrackNetwork = () => {
  const userProfileCacheRef = useRef(new Map());
  const [confirmModal, setConfirmModal] = useState(false);
  const [userReward, setUserReward] = useState("");
  const [adminReward, setAdminReward] = useState(0);
  const [allData, setAllData] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedAssetData, setSelectedAssetData] = useState(null);

  const [editStatusModal, setEditStatusModal] = useState(false);
  const [selectedStatusAssetId, setSelectedStatusAssetId] = useState("");

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [selectedTipData, setSelectedTipData] = useState(null);
  const [selectedTipTitle, setSelectedTipTitle] = useState("Tip Details");

  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) {
      return null;
    }

    if (userProfileCacheRef.current.has(userId)) {
      return userProfileCacheRef.current.get(userId);
    }

    try {
      const response = await apiClient.get(`/users/${userId}`);
      const profile = response?.data || null;
      userProfileCacheRef.current.set(userId, profile);
      return profile;
    } catch (error) {
      console.log("[HelpTrackNetwork] user profile fetch failed", { userId, error });
      return null;
    }
  }, []);

  const getData = useCallback(async () => {
    try {
      const response = await apiClient.get("/help-track/lostassets");
      const assets = Array.isArray(response?.data) ? response.data : [];

      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const isSuperAdmin = Boolean(storedUser?.superAdmin);
      const selectedState = normalizeText(storedUser?.state);

      const rows = await Promise.all(assets.map((asset) => mapAssetToRow(asset, fetchUserProfile)));
      const filteredRows = rows.filter((row) => {
        const rowState = normalizeText(row?.state);
        if (!isSuperAdmin) {
          return !selectedState || rowState === selectedState;
        }

        return true;
      });

      setAllData(filteredRows);
    } catch (error) {
      console.log("[HelpTrackNetwork] failed to fetch lost assets", error);
      setAllData([]);
    }
  }, [fetchUserProfile]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

    if (storedUser?.userId) {
      void socketService.initializeSocket(storedUser.userId, storedUser.state, {
        isSuperAdmin: storedUser.superAdmin,
        web: "true",
      }).catch(() => {});
    }

    getData();
  }, [getData]);

  const openReportsModal = (reports) => {
    const reportList = normalizeMixedList(reports);
    setSelectedReports(reportList);
    setReportModalOpen(true);
  };

  const closeReportsModal = () => {
    setReportModalOpen(false);
    setSelectedReports([]);
  };

  const openTipModal = (item) => {
    const tipData = item?.tipDetails || null;
    setSelectedTipData(tipData);
    setSelectedTipTitle(
      item?.caseId && item?.caseId !== "N/A" ? `Tip Details - ${item.caseId}` : "Tip Details",
    );
    setTipModalOpen(true);
  };

  const closeTipModal = () => {
    setTipModalOpen(false);
    setSelectedTipData(null);
    setSelectedTipTitle("Tip Details");
  };

  const openConfirm = (item) => {
    // console.log("item", item);
    setSelectedAssetId(item.assetId);
    setSelectedAssetData(item);
    setUserReward(normalizeRewardValue(item.reward));
    setAdminReward(item.adminAddReward || 0);
    setConfirmModal(true);
  };

  const closeConfirm = () => {
    setConfirmModal(false);
    setSelectedAssetData(null);
  };
  const formatCurrency = (num) => "₦" + Number(num || 0).toLocaleString("en-NG");
  const totalReward = Number(userReward || 0) + Number(adminReward || 0);

  const handleImageClick = (imgUrl) => {
    setOnClickImageData(imgUrl);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setOnClickImageData("");
  };

  const handleApprove = async (assetId, useId) => {
    try {
      // Legacy Firebase update kept for reference:
      // const db = getDatabase();
      // const assetRef = ref(db, `lostassets/${assetId}`);
      // await update(assetRef, { status: true });
      await apiClient.patch(`/help-track/lostassets/${assetId}`, { status: true });
      toast.success(`Help Track Network Approved Successfully!`);
      socketService.emit("sendcustompushnotification", {
        userId: useId,
        title: "Help Track Network Approved",
        message: "We're informing you that your Help Track Network submission has been approved",
        action: "helptracknetwork",
      });
      getData();
    } catch (error) {
      toast.error("Error approving Help Track Network.");
    }
  };

  const handleReject = async (assetId, useId) => {
    try {
      // Legacy Firebase update kept for reference:
      // const db = getDatabase();
      // const assetRef = ref(db, `lostassets/${assetId}`);
      // await update(assetRef, { status: false });
      await apiClient.patch(`/help-track/lostassets/${assetId}`, { status: false });

      toast.success(`Help Track Network Rejected Successfully!`);
      socketService.emit("sendcustompushnotification", {
        userId: useId,
        title: "Help Track Network Rejected",
        message:
          "We're sorry to inform you that your Help Track Network submission has been rejected. Please contact support for more information.",
        action: "helptracknetwork",
      });
      getData();
    } catch (error) {
      toast.error("Error rejecting Help Track Network.");
    }
  };

  const openEditStatusModal = (assetId) => {
    setSelectedStatusAssetId(assetId);
    setEditStatusModal(true);
  };

  const closeEditStatusModal = () => {
    setSelectedStatusAssetId("");
    setEditStatusModal(false);
  };

  const handleUpdateStatus = async (isFound) => {
    try {
      // Legacy Firebase update kept for reference:
      // const db = getDatabase();
      // const assetRef = ref(db, `lostassets/${selectedStatusAssetId}`);
      // await update(assetRef, { assetsFound: isFound });
      await apiClient.patch(`/help-track/lostassets/${selectedStatusAssetId}`, {
        assetsFound: isFound,
      });
      toast.success(`Status updated to ${isFound ? "Found" : "Not Found"} successfully!`);
      closeEditStatusModal();
      getData();
    } catch (error) {
      toast.error("Error updating status.");
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={10} pb={3}>
        <ToastContainer />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ textAlign: "center" }}
              >
                <MDTypography variant="h5" color="white">
                  HelpTrack Network Dashboard
                </MDTypography>
              </MDBox>

              <MDBox>
                <TableContainer component={Paper}>
                  <Table sx={{ "& td, & th": { fontSize: "11px", padding: "8px" } }}>
                    <TableBody>
                      <TableRow>
                        {[
                          "S/N",
                          "Name",
                          "Phone",
                          "Email",
                          "Vehicle Type",
                          "Brand/Model",
                          "Plate Number",
                          "Color",
                          "Photo",
                          "Proof of Ownership",
                          "Last Seen Location",
                          "Date & Time",
                          "Case ID",
                          "Additional Information",
                          "Report Sighting",
                          // "Tip",
                          "Reward",
                          "Status",
                          "Action",
                        ].map((label) => (
                          <TableCell key={label} align="center" sx={{ fontWeight: "bold" }}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>

                      {allData.length > 0 ? (
                        allData.map((item, index) => (
                          <TableRow key={item.assetId || index}>
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell align="center">{item.name}</TableCell>
                            <TableCell align="center">{item.phone}</TableCell>
                            <TableCell align="center">{item.email}</TableCell>
                            <TableCell align="center">{item.vehicleType}</TableCell>
                            <TableCell align="center">{item.brand}</TableCell>
                            <TableCell align="center">{item.plate}</TableCell>
                            <TableCell align="center">{item.color}</TableCell>

                            <TableCell align="center">
                              {item.photo && item.photo.length > 0 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                  }}
                                >
                                  {item.photo.slice(0, 3).map((img, idx) => (
                                    <img
                                      key={idx}
                                      src={img}
                                      alt={`photo-${idx}`}
                                      width="50"
                                      height="35"
                                      style={{ borderRadius: 6, cursor: "pointer" }}
                                      onClick={() => handleImageClick(img)}
                                    />
                                  ))}
                                  {item.photo.length > 3 && (
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#007bff",
                                        cursor: "pointer",
                                        alignSelf: "center",
                                      }}
                                      onClick={() => handleImageClick(item.photo[0])}
                                    >
                                      +{item.photo.length - 3} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>

                            <TableCell align="center">
                              {item.ownershipImg ? (
                                <img
                                  src={item.ownershipImg}
                                  alt="ownership"
                                  width="50"
                                  height="35"
                                  style={{ borderRadius: 6, cursor: "pointer" }}
                                  onClick={() => handleImageClick(item.ownershipImg)}
                                />
                              ) : (
                                "N/A"
                              )}
                            </TableCell>

                            <TableCell align="center">{item?.location || "N/A"}</TableCell>
                            <TableCell align="center">{item?.datetime || "N/A"}</TableCell>
                            <TableCell align="center">{item?.caseId || "N/A"}</TableCell>
                            <TableCell align="center">{item?.additionalInfo || "N/A"}</TableCell>

                            <TableCell align="center">
                              {item.reports ? (
                                <button
                                  className="btn btn-info btn-sm"
                                  style={{ fontSize: "10px", padding: "3px 6px", color: "white" }}
                                  onClick={() => openReportsModal(item.reports)}
                                >
                                  Show
                                </button>
                              ) : (
                                "No Reports"
                              )}
                            </TableCell>

                            {/* <TableCell align="center">
                              {item.tipDetails ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <button
                                    className="btn btn-warning btn-sm"
                                    style={{ fontSize: "10px", padding: "3px 6px" }}
                                    onClick={() => openTipModal(item)}
                                  >
                                    Show
                                  </button>
                                  {item.tipAmount !== null ? (
                                    <span style={{ fontSize: "11px", fontWeight: "bold" }}>
                                      {formatCurrency(item.tipAmount)}
                                    </span>
                                  ) : null}
                                </div>
                              ) : item.tipAmount !== null ? (
                                formatCurrency(item.tipAmount)
                              ) : (
                                "N/A"
                              )}
                            </TableCell> */}

                            <TableCell align="center">
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  background: "#FFD740",
                                  padding: "5px 8px",
                                  borderRadius: "6px",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                }}
                                onClick={() => openConfirm(item)}
                              >
                                {formatCurrency(Number(item.reward || 0) + Number(item.adminAddReward || 0))}
                                <img
                                  src={require("../../assets/edit.png")}
                                  alt="edit"
                                  width="16"
                                  height="16"
                                />
                              </span>
                            </TableCell>

                            <TableCell align="center">
                              <span
                                style={{
                                  background: item.assetsFound ? "green" : "red",
                                  color: "white",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontWeight: "bold",
                                  lineHeight: "20px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  cursor: "pointer",
                                  minWidth: "80px",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                }}
                                onClick={() => openEditStatusModal(item.assetId)}
                              >
                                {item.assetsFound ? "Found" : "Not Found"}
                                <img
                                  src={require("../../assets/edit.png")}
                                  alt="edit-status"
                                  width="12"
                                  height="12"
                                />
                              </span>
                            </TableCell>

                            <TableCell align="center">
                              {item.status ? (
                                <span
                                  style={{
                                    background: "green",
                                    color: "white",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    fontWeight: "bold",
                                    fontSize: "10px",
                                  }}
                                >
                                  Approved
                                </span>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-success btn-sm mb-1"
                                    style={{ marginRight: "6px" }}
                                    onClick={() => handleApprove(item.assetId, item.userId)}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm "
                                    onClick={() => handleReject(item.assetId, item.userId)}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={19} align="center">
                            No records found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={confirmModal} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Broadcast</DialogTitle>
        <DialogContent sx={{ fontSize: "14px", paddingTop: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
            <span>
              <b>Reward:</b>
            </span>
            <span>{formatCurrency(userReward)}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <span>
              <b>Admin Added Reward:</b>
            </span>
            <input
              type="number"
              min="0"
              value={adminReward}
              onChange={(e) => setAdminReward(Number(e.target.value) || 0)}
              placeholder="0"
              style={{
                width: "90px",
                padding: "5px 6px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                textAlign: "right",
                fontWeight: "bold",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
            <span>
              <b>Total Reward:</b>
            </span>
            <span style={{ color: "green", fontWeight: "bold" }}>
              {formatCurrency(totalReward)}
            </span>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
            <span>
              <b>User Tip:</b>
            </span>
            <span style={{ color: "#8a5a00", fontWeight: "bold" }}>
              {selectedAssetData?.tipAmount !== null && selectedAssetData?.tipAmount !== undefined
                ? formatCurrency(selectedAssetData.tipAmount)
                : "N/A"}
            </span>
          </div>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", padding: "10px 20px" }}>
          <button className="btn btn-secondary btn-sm" onClick={closeConfirm}>
            Cancel
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={async () => {
              try {
                // Legacy Firebase update kept for reference:
                // const db = getDatabase();
                // const assetRef = ref(db, `lostassets/${selectedAssetId}`);
                // await update(assetRef, { adminAddReward: Number(adminReward) });
                await apiClient.patch(`/help-track/lostassets/${selectedAssetId}`, {
                  adminAddReward: Number(adminReward),
                });
                toast.success("Admin reward updated successfully!");
                closeConfirm();
                getData();
              } catch (error) {
                toast.error("Failed to update admin reward.");
              }
            }}
          >
            Update
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={editStatusModal} onClose={closeEditStatusModal} maxWidth="xs">
        <DialogContent sx={{ textAlign: "center", padding: "20px" }}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}
          >
            <button
              className="btn btn-success btn-sm"
              style={{ width: "120px" }}
              onClick={() => handleUpdateStatus(true)}
            >
              Found
            </button>
            <button
              className="btn btn-danger btn-sm"
              style={{ width: "120px" }}
              onClick={() => handleUpdateStatus(false)}
            >
              Not Found
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reportModalOpen} onClose={closeReportsModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Sightings Reported</DialogTitle>

        <DialogContent>
          {selectedReports.length > 0 ? (
            <Table sx={{ "& td, & th": { fontSize: "12px", padding: "8px" } }}>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <b>Date/Time Seen</b>
                  </TableCell>
                  <TableCell>
                    <b>Name</b>
                  </TableCell>
                  <TableCell>
                    <b>User ID</b>
                  </TableCell>
                  <TableCell>
                    <b>Image</b>
                  </TableCell>
                  <TableCell>
                    <b>Location</b>
                  </TableCell>
                  <TableCell>
                    <b>Phone Number</b>
                  </TableCell>
                  <TableCell>
                    <b>Details</b>
                  </TableCell>
                  <TableCell>
                    <b>Photos</b>
                  </TableCell>
                </TableRow>

                {selectedReports.map((rep, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{rep?.dateTimeSeen || "N/A"}</TableCell>
                    <TableCell>{rep?.reportUserName || "N/A"}</TableCell>
                    <TableCell>{rep?.reportUserId || "N/A"}</TableCell>
                    <TableCell>
                      {rep?.reportUserImage ? (
                        <img
                          src={rep.reportUserImage}
                          alt="Reporter"
                          width="50"
                          height="50"
                          style={{ borderRadius: "6px", cursor: "pointer" }}
                          onClick={() => handleImageClick(rep.reportUserImage)}
                        />
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>{rep?.location || "N/A"}</TableCell>
                    <TableCell>{rep?.reportUserMobileNo || "N/A"}</TableCell>
                    <TableCell>{rep?.details || "N/A"}</TableCell>

                    <TableCell>
                      {rep?.photos && rep?.photos?.length > 0 ? (
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          {rep?.photos?.map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              width="50"
                              height="40"
                              style={{ borderRadius: 5, cursor: "pointer" }}
                              onClick={() => handleImageClick(img)}
                            />
                          ))}
                        </div>
                      ) : (
                        "No Photos"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p>No sightings reported.</p>
          )}
        </DialogContent>

        <DialogActions>
          <button className="btn btn-secondary btn-sm" onClick={closeReportsModal}>
            Close
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={tipModalOpen} onClose={closeTipModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>{selectedTipTitle}</DialogTitle>
        <DialogContent>
          {selectedTipData ? (
            <Table sx={{ "& td, & th": { fontSize: "12px", padding: "8px" } }}>
              <TableBody>
                {Object.entries(selectedTipData).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: "bold", width: "28%" }}>{key}</TableCell>
                    <TableCell>
                      {value && typeof value === "object" ? (
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "12px",
                          }}
                        >
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      ) : (
                        String(value ?? "N/A")
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p>No tip details found.</p>
          )}
        </DialogContent>
        <DialogActions>
          <button className="btn btn-secondary btn-sm" onClick={closeTipModal}>
            Close
          </button>
        </DialogActions>
      </Dialog>

      <ImageModal
        onClickImageData={onClickImageData}
        onClose={handleCloseModal}
        isModalOpen={isModalOpen}
      />
    </DashboardLayout>
  );
};

export default HelpTrackNetwork;
